import { useState, useMemo, useCallback, memo } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { BadgeAlert, BellDot, CheckCircle, CircleAlertIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "react-toastify";
import { useAuth } from "@/context/AuthContext";
import { markNoteAsRead } from "@/lib/collections/WorkSessionNotes/markAsRead";
import { useRealtimeNotes } from "@/hooks/realtime/realtimeNotifcations";
import { useUsersMap } from "@/hooks/ComponentNotification/hookNotification";

const Notifications = memo(() => {
  const { userRoles } = useAuth();
  const dbId = import.meta.env.VITE_APPWRITE_DATABASE_ID!;
  const colId = import.meta.env.VITE_WORK_SESSION_NOTES_COLLECTION_ID!;
  const [_selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isMarking, setIsMarking] = useState(false);
  const [notes, isLoading] = useRealtimeNotes(dbId, colId);
  const usersMap = useUsersMap();                // mapa de usuarios

  // Memoizar agrupación de notas por usuario
  const notesByUser = useMemo(() => {
    return notes.reduce<Record<string, typeof notes>>((acc, note) => {
      const uid = note.userId;
      acc[uid] = acc[uid] ? [...acc[uid], note] : [note];
      return acc;
    }, {});
  }, [notes]);

  const handleMarkAsRead = useCallback(async (noteId: string) => {
    if (isMarking) return;

    try {
      setIsMarking(true);
      await markNoteAsRead(noteId);
      toast.success("El mensaje ha sido leído.");
    } catch {
      toast.error("No se pudo marcar como leído.");
    } finally {
      setIsMarking(false);
    }
  }, [isMarking]);

  if (!userRoles.isRRHH && !userRoles.isAdmin) return null;
  if (isLoading) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="relative cursor-pointer shadow-lg">
          <BellDot />
          {notes.length > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-yellow-500 rounded-full">
              {notes.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-60 max-h-64 overflow-auto">
        <DropdownMenuLabel className="text-center font-nunito">Notificaciones</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {Object.entries(notesByUser).map(([uid, userNotes]) => {
          const user = usersMap.get(uid);
          const displayName = user?.name;  // <— aquí usas el nombre
          return (
            <Dialog key={uid} onOpenChange={(open) => !open && setSelectedUserId(null)}>
              <DialogTrigger asChild>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-500 rounded-lg font-nunito text-left cursor-pointer"
                  onClick={() => setSelectedUserId(uid)}
                >
                  <BadgeAlert className="w-5 h-5 text-yellow-500" />
                  {displayName} ({userNotes.length})
                </button>
              </DialogTrigger>

              <DialogContent className="max-w-[40vh] max-h-[70vh] overflow-auto">
                <DialogHeader>
                  <DialogTitle className="font-bold text-xl text-yellow-600">
                    Notas de {displayName}
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-4 space-y-4">
                  {userNotes.map((note) => (
                    <div
                      key={note.$id}
                      className="flex justify-between items-start gap-3 p-3 border rounded font-nunito"
                    >
                      <div className="flex gap-2">
                        <CircleAlertIcon className="w-5 h-5 mt-1 text-yellow-500" />
                        <div>
                          <p className="wrap-break-word">{note.noteContent}</p>
                          <small className="block text-gray-500">
                            <span className="text-yellow-500">Enviado: </span>
                            {new Date(note.createdAt).toLocaleString("es-ES", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}

                          </small>
                        </div>
                      </div>
                      <button
                        onClick={() => handleMarkAsRead(note.$id)}
                        disabled={isMarking}
                        title="Marcar como leído"
                        className="cursor-pointer hover:text-green-800 text-green-600"
                      >
                        <CheckCircle className="w-5 h-5 mt-1" />
                      </button>
                    </div>
                  ))}
                  {userNotes.length === 0 && <p className="text-center text-gray-500">Sin nuevas notas</p>}
                </div>
              </DialogContent>
            </Dialog>
          );
        })}

        {notes.length === 0 && <p className="p-4 text-center text-gray-500">No hay notificaciones</p>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

Notifications.displayName = 'Notifications';

export default Notifications;
